/**
 * Promotions Management API Hooks
 * React hooks for managing promotions data
 */

import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pricingApiClient } from './pricingApiClient';
import {
  Promotion,
  PromotionDTO,
  PromotionListQueryParams,
  PagedData,
  PromotionForm
} from './promotionsTypes';
import { ApiResponse } from './types';
import { CityMaster, VehicleCategory } from './pricingTypes';

/**
 * Hook to fetch a single promotion by ID
 * @param id - The promotion ID
 * @param options - Hook options
 */
export function usePromotion(
  id: number,
  options: {
    enabled?: boolean;
    onSuccess?: (data: Promotion) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const response = await pricingApiClient.get<ApiResponse<Promotion>>(`/promotions/${id}`);

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    return response.data as Promotion;
  }, [id]);

  return useQuery<Promotion>({
    queryKey: ['promotion', id],
    queryFn,
    enabled: options.enabled !== false && !!id,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch paginated list of promotions
 * @param params - Query parameters (page, size, sort, search)
 * @param options - Hook options
 */
export function usePromotions(
  params: PromotionListQueryParams = {},
  options: {
    enabled?: boolean;
    onSuccess?: (data: PagedData<Promotion>) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const queryParams: Record<string, any> = {};

    if (params.page !== undefined) {
      queryParams.page = params.page;
    }
    if (params.size !== undefined) {
      queryParams.size = params.size;
    }
    if (params.search) {
      queryParams.search = params.search;
    }
    if (params.vehicleCategoryId) {
      queryParams.vehicleCategoryId = params.vehicleCategoryId;
    }
    if (params.cityId) {
      queryParams.cityId = params.cityId;
    }
    if (params.isActive !== undefined) {
      queryParams.isActive = params.isActive;
    }
    if (params.discountType) {
      queryParams.discountType = params.discountType;
    }
    if (params.applicableUserType) {
      queryParams.applicableUserType = params.applicableUserType;
    }
    if (params.validFrom) {
      queryParams.validFrom = params.validFrom;
    }
    if (params.validTo) {
      queryParams.validTo = params.validTo;
    }
    if (params.sort && params.sort.length > 0) {
      queryParams.sort = params.sort;
    }

    const response = await pricingApiClient.get<ApiResponse<PagedData<Promotion>>>(
      '/promotions/list',
      queryParams
    );

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    return response.data as PagedData<Promotion>;
  }, [params.page, params.size, params.sort, params.search, params.vehicleCategoryId, params.cityId, params.isActive, params.discountType, params.applicableUserType, params.validFrom, params.validTo]);

  return useQuery<PagedData<Promotion>>({
    queryKey: ['promotions', params],
    queryFn,
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch all promotions (no pagination)
 * @param options - Hook options
 */
export function useAllPromotions(
  options: {
    enabled?: boolean;
    onSuccess?: (data: Promotion[]) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const response = await pricingApiClient.get<ApiResponse<Promotion[]>>(`/promotions/list-only`);

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    return response.data as Promotion[];
  }, []);

  return useQuery<Promotion[]>({
    queryKey: ['all-promotions'],
    queryFn,
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch active promotions only
 * @param options - Hook options
 */
export function useActivePromotions(
  options: {
    enabled?: boolean;
    onSuccess?: (data: Promotion[]) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const response = await pricingApiClient.get<ApiResponse<Promotion[]>>(`/promotions/active-list-only`);

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    return response.data as Promotion[];
  }, []);

  return useQuery<Promotion[]>({
    queryKey: ['active-promotions'],
    queryFn,
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to create a new promotion
 * @param options - Hook options
 */
export function useCreatePromotion(
  options: {
    onSuccess?: (data: ApiResponse<Promotion>, variables: PromotionDTO) => void;
    onError?: (error: Error, variables: PromotionDTO) => void;
  } = {}
) {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<Promotion>, Error, PromotionDTO>({
    mutationFn: async (promotionData: PromotionDTO) => {
      const response = await pricingApiClient.post<ApiResponse<Promotion>>(
        '/promotions',
        promotionData
      );
      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate all promotion queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      queryClient.invalidateQueries({ queryKey: ['all-promotions'] });
      queryClient.invalidateQueries({ queryKey: ['active-promotions'] });
      
      if (options.onSuccess) {
        options.onSuccess(data, variables);
      }
    },
    onError: options.onError,
  });
}

/**
 * Hook to update an existing promotion
 * @param options - Hook options
 */
export function useUpdatePromotion(
  options: {
    onSuccess?: (data: ApiResponse<Promotion>, variables: { id: number; data: PromotionDTO }) => void;
    onError?: (error: Error, variables: { id: number; data: PromotionDTO }) => void;
  } = {}
) {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<Promotion>, Error, { id: number; data: PromotionDTO }>({
    mutationFn: async ({ id, data }) => {
      const response = await pricingApiClient.put<ApiResponse<Promotion>>(
        `/promotions/${id}`,
        data
      );
      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate all promotion queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      queryClient.invalidateQueries({ queryKey: ['all-promotions'] });
      queryClient.invalidateQueries({ queryKey: ['active-promotions'] });
      queryClient.invalidateQueries({ queryKey: ['promotion', variables.id] });
      
      if (options.onSuccess) {
        options.onSuccess(data, variables);
      }
    },
    onError: options.onError,
  });
}

/**
 * Hook to delete a promotion
 * @param options - Hook options
 */
export function useDeletePromotion(
  options: {
    onSuccess?: (data: ApiResponse<Promotion>, promotionId: number) => void;
    onError?: (error: Error, promotionId: number) => void;
  } = {}
) {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<Promotion>, Error, number>({
    mutationFn: async (promotionId: number) => {
      const response = await pricingApiClient.delete<ApiResponse<Promotion>>(
        `/promotions/${promotionId}`
      );
      return response;
    },
    onSuccess: (data, promotionId) => {
      // Invalidate all promotion queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      queryClient.invalidateQueries({ queryKey: ['all-promotions'] });
      queryClient.invalidateQueries({ queryKey: ['active-promotions'] });
      queryClient.invalidateQueries({ queryKey: ['promotion', promotionId] });
      
      if (options.onSuccess) {
        options.onSuccess(data, promotionId);
      }
    },
    onError: options.onError,
  });
}

/**
 * Hook to toggle promotion active status
 * @param options - Hook options
 */
export function useTogglePromotionStatus(
  options: {
    onSuccess?: (data: ApiResponse<Promotion>, promotionId: number) => void;
    onError?: (error: Error, promotionId: number) => void;
  } = {}
) {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<Promotion>, Error, number>({
    mutationFn: async (promotionId: number) => {
      const response = await pricingApiClient.get<ApiResponse<Promotion>>(
        `/promotions/toggle-active-status/${promotionId}`
      );
      return response;
    },
    onSuccess: (data, promotionId) => {
      // Invalidate all promotion queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      queryClient.invalidateQueries({ queryKey: ['all-promotions'] });
      queryClient.invalidateQueries({ queryKey: ['active-promotions'] });
      queryClient.invalidateQueries({ queryKey: ['promotion', promotionId] });
      
      if (options.onSuccess) {
        options.onSuccess(data, promotionId);
      }
    },
    onError: options.onError,
  });
}

/**
 * Utility function to determine promotion status based on dates
 */
export function getPromotionStatus(promotion: Promotion): 'active' | 'scheduled' | 'expired' | 'inactive' {
  if (!promotion.isActive) {
    return 'inactive';
  }

  const now = new Date();
  const validFrom = new Date(promotion.validFrom);
  const validTo = new Date(promotion.validTo);

  if (now < validFrom) {
    return 'scheduled';
  } else if (now > validTo) {
    return 'expired';
  } else {
    return 'active';
  }
}

/**
 * Utility function to check if a promotion is currently valid
 */
export function isPromotionCurrentlyValid(promotion: Promotion): boolean {
  if (!promotion.isActive) {
    return false;
  }

  const now = new Date();
  const validFrom = new Date(promotion.validFrom);
  const validTo = new Date(promotion.validTo);

  return now >= validFrom && now <= validTo;
}

// Re-export existing hooks that are also needed for promotions
export { useCities, useVehicleCategories } from './usePricing';